'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { DayPicker } from 'react-day-picker';
import { de } from 'react-day-picker/locale';

type DateInputProps = {
  /** Selected value and onChange use DD.MM.YYYY. */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  triggerOnly?: boolean;
  triggerLabel?: string;
  id?: string;
  name?: string;
  min?: string;
  max?: string;
  inputRef?: (instance: HTMLInputElement | null) => void;
  'aria-required'?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
};

type PickerView = 'days' | 'months' | 'years';

function formatDate(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

function parseDate(str: string): Date | undefined {
  const match = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return undefined;
  const day = Number(match[1]);
  const month = Number(match[2]) - 1;
  const year = Number(match[3]);
  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

function parseIsoDate(value?: string): Date | undefined {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return date;
}

function monthLabel(date: Date): string {
  const label = new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(
    date
  );
  return label.charAt(0).toLocaleUpperCase('de-DE') + label.slice(1);
}

function formatTypedDate(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

function caretAfterDigits(value: string, digitCount: number): number {
  if (digitCount === 0) return 0;
  let seen = 0;
  for (let index = 0; index < value.length; index++) {
    if (!/\d/.test(value[index])) continue;
    seen++;
    if (seen === digitCount) {
      return value[index + 1] === '.' ? index + 2 : index + 1;
    }
  }
  return value.length;
}

export function DateInput({
  value = '',
  onChange,
  placeholder = 'TT.MM.JJJJ',
  className = '',
  inputClassName = 'w-full min-h-11 border border-ink bg-transparent font-mono text-base sm:text-sm font-bold px-3 py-2 pr-12 placeholder:text-ink/30',
  buttonClassName = 'inline-flex items-center justify-center border-l border-ink px-3 hover:text-signal transition-colors',
  triggerOnly = false,
  triggerLabel = 'Kalender öffnen',
  id,
  name,
  min,
  max,
  inputRef,
  'aria-required': ariaRequired,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: DateInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  const [draft, setDraft] = useState<Date | undefined>(() => parseDate(value));
  const [displayMonth, setDisplayMonth] = useState<Date>(
    () => parseDate(value) ?? new Date()
  );
  const [view, setView] = useState<PickerView>('days');
  const [yearStart, setYearStart] = useState(
    () =>
      Math.floor(
        (parseDate(value)?.getFullYear() ?? new Date().getFullYear()) / 12
      ) * 12
  );
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pendingCaret = useRef<{
    input: HTMLInputElement;
    position: number;
  } | null>(null);
  const [dialogPosition, setDialogPosition] = useState({ top: 8, left: 8 });

  const selected = parseDate(value);
  const minDate = useMemo(() => parseIsoDate(min), [min]);
  const maxDate = useMemo(() => parseIsoDate(max), [max]);
  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, month) =>
        monthLabel(new Date(2026, month, 1))
      ),
    []
  );

  if (lastValue !== value) {
    setLastValue(value);
    setInputValue(value);
  }

  useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (!pending) return;
    pending.input.setSelectionRange(pending.position, pending.position);
    pendingCaret.current = null;
  }, [inputValue]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();

    function positionDialog() {
      if (window.matchMedia('(max-width: 767px)').matches) return;
      const bounds = triggerRef.current?.getBoundingClientRect();
      if (!bounds || !dialog) return;
      const width = dialog.getBoundingClientRect().width;
      const height = dialog.getBoundingClientRect().height;
      setDialogPosition({
        top: Math.max(
          8,
          Math.min(bounds.bottom + 4, window.innerHeight - height - 8)
        ),
        left: Math.max(
          8,
          Math.min(bounds.right - width, window.innerWidth - width - 8)
        ),
      });
    }
    positionDialog();
    window.addEventListener('resize', positionDialog);
    return () => window.removeEventListener('resize', positionDialog);
  }, [isOpen]);

  function openPicker() {
    const initial = selected ?? new Date();
    setDraft(selected);
    setDisplayMonth(new Date(initial.getFullYear(), initial.getMonth(), 1));
    setYearStart(Math.floor(initial.getFullYear() / 12) * 12);
    setView('days');
    setIsOpen(true);
  }

  function closePicker(commit: boolean) {
    if (commit && draft) {
      const nextValue = formatDate(draft);
      setInputValue(nextValue);
      onChange?.(nextValue);
    }
    dialogRef.current?.close();
    setIsOpen(false);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const rawValue = event.target.value;
    if (rawValue.replace(/\D/g, '').length > 8) {
      event.target.value = inputValue;
      pendingCaret.current = null;
      return;
    }
    const digitsBeforeCaret = rawValue
      .slice(0, event.target.selectionStart ?? rawValue.length)
      .replace(/\D/g, '').length;
    const nextValue = formatTypedDate(rawValue);
    pendingCaret.current = {
      input: event.target,
      position: caretAfterDigits(nextValue, digitsBeforeCaret),
    };
    setInputValue(nextValue);
    onChange?.(nextValue);
    const parsed = parseDate(nextValue);
    if (parsed) {
      setDraft(parsed);
      setDisplayMonth(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
    }
  }

  function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const caret = event.currentTarget.selectionStart;
    if (
      event.key !== 'Backspace' ||
      caret === null ||
      caret !== event.currentTarget.selectionEnd ||
      inputValue[caret - 1] !== '.'
    )
      return;

    event.preventDefault();
    const digitsBeforeCaret = inputValue
      .slice(0, caret - 2)
      .replace(/\D/g, '').length;
    const nextValue = formatTypedDate(
      inputValue.slice(0, caret - 2) + inputValue.slice(caret)
    );
    pendingCaret.current = {
      input: event.currentTarget,
      position: caretAfterDigits(nextValue, digitsBeforeCaret),
    };
    setInputValue(nextValue);
    onChange?.(nextValue);
  }

  function selectYear(year: number) {
    if (
      (minDate && year < minDate.getFullYear()) ||
      (maxDate && year > maxDate.getFullYear())
    ) {
      return;
    }
    setDisplayMonth((current) => new Date(year, current.getMonth(), 1));
    setView('days');
  }

  function changeMonth(offset: number) {
    setDisplayMonth(
      (current) =>
        new Date(current.getFullYear(), current.getMonth() + offset, 1)
    );
  }

  const previousMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() - 1,
    1
  );
  const nextMonth = new Date(
    displayMonth.getFullYear(),
    displayMonth.getMonth() + 1,
    1
  );
  const canGoPrevious =
    !minDate ||
    previousMonth >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canGoNext =
    !maxDate ||
    nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const dialog = isOpen ? (
    <dialog
      ref={dialogRef}
      aria-labelledby={`${id ?? 'date-input'}-dialog-title`}
      className="date-input-dialog fixed inset-auto m-0 box-border w-[360px] max-w-[calc(100vw-16px)] max-h-[calc(100dvh-16px)] overflow-y-auto border border-ink bg-paper p-3 text-ink backdrop:bg-transparent max-md:!inset-x-0 max-md:!bottom-0 max-md:!top-auto max-md:!left-0 max-md:w-full max-md:max-w-none max-md:max-h-[92dvh] max-md:border-x-0 max-md:border-b-0 max-md:border-t-2 max-md:px-4 max-md:pb-[max(16px,env(safe-area-inset-bottom))] max-md:backdrop:bg-ink/45"
      style={{ top: dialogPosition.top, left: dialogPosition.left }}
      onCancel={(event) => {
        event.preventDefault();
        closePicker(false);
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) closePicker(false);
      }}
      onClose={() => setIsOpen(false)}
    >
      <div className="flex items-center justify-between border-b border-ink pb-3">
        <div>
          <h2
            id={`${id ?? 'date-input'}-dialog-title`}
            className="font-mono text-sm font-bold uppercase m-0"
          >
            Datum wählen
          </h2>
          <p
            aria-live="polite"
            className="font-mono text-xs opacity-60 m-0 mt-1"
          >
            {draft
              ? new Intl.DateTimeFormat('de-DE', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }).format(draft)
              : 'Kein Datum ausgewählt'}
          </p>
        </div>
        <button
          type="button"
          aria-label="Kalender schließen"
          onClick={() => closePicker(false)}
          className="min-h-11 min-w-11 font-mono text-lg"
        >
          ×
        </button>
      </div>

      {view === 'days' && (
        <>
          <div className="flex items-center justify-between py-2">
            <button
              type="button"
              aria-label="Zum vorherigen Monat"
              disabled={!canGoPrevious}
              onClick={() => changeMonth(-1)}
              className="flex min-h-11 min-w-11 items-center justify-center font-mono text-2xl font-bold hover:bg-ink hover:text-paper disabled:opacity-25"
            >
              ‹
            </button>
            <div className="flex items-center justify-center gap-1">
              <button
                type="button"
                aria-label="Monat auswählen"
                onClick={() => setView('months')}
                className="min-h-11 px-2 font-mono text-sm font-bold uppercase hover:bg-zinc-200"
              >
                {monthLabel(displayMonth)}
              </button>
              <button
                type="button"
                aria-label="Jahr auswählen"
                onClick={() => setView('years')}
                className="min-h-11 px-2 font-mono text-sm font-bold hover:bg-zinc-200"
              >
                {displayMonth.getFullYear()}
              </button>
            </div>
            <button
              type="button"
              aria-label="Zum nächsten Monat"
              disabled={!canGoNext}
              onClick={() => changeMonth(1)}
              className="flex min-h-11 min-w-11 items-center justify-center font-mono text-2xl font-bold hover:bg-ink hover:text-paper disabled:opacity-25"
            >
              ›
            </button>
          </div>
          <DayPicker
            locale={de}
            mode="single"
            selected={draft}
            month={displayMonth}
            hideNavigation
            onSelect={(date) => date && setDraft(date)}
            disabled={
              minDate && maxDate
                ? { before: minDate, after: maxDate }
                : minDate
                  ? { before: minDate }
                  : maxDate
                    ? { after: maxDate }
                    : undefined
            }
            startMonth={minDate}
            endMonth={maxDate}
            components={{ MonthCaption: () => <div aria-hidden="true" /> }}
            classNames={{
              root: 'w-full',
              month: 'w-full',
              month_grid: 'w-full table-fixed border-collapse',
              weekday: 'font-mono text-xs text-center py-1 opacity-50',
              day: 'font-mono text-xs font-bold text-center p-0',
              day_button:
                'w-full min-h-11 font-mono text-sm font-bold hover:bg-ink hover:text-paper transition-colors',
              selected: '!bg-signal !text-paper',
              today: 'border border-ink',
              disabled: 'opacity-25 cursor-not-allowed',
            }}
          />
        </>
      )}

      {view === 'months' && (
        <div className="py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs font-bold uppercase opacity-60">
              Monat wählen
            </span>
            <button
              type="button"
              onClick={() => setView('years')}
              className="min-h-11 px-2 font-mono text-sm font-bold hover:bg-zinc-200"
            >
              {displayMonth.getFullYear()}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {monthNames.map((monthName, month) => (
              <button
                key={monthName}
                type="button"
                aria-pressed={displayMonth.getMonth() === month}
                onClick={() => {
                  setDisplayMonth(
                    (current) => new Date(current.getFullYear(), month, 1)
                  );
                  setView('days');
                }}
                className={`min-h-11 border border-ink px-2 font-mono text-xs font-bold uppercase ${displayMonth.getMonth() === month ? 'bg-ink text-paper' : 'hover:bg-zinc-200'}`}
              >
                {monthName.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
      )}

      {view === 'years' && (
        <div className="py-3">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              aria-label="Vorherige Jahre"
              onClick={() => setYearStart((value) => value - 12)}
              className="min-h-11 min-w-11 font-mono text-lg"
            >
              ‹
            </button>
            <span className="font-mono text-xs font-bold uppercase opacity-60">
              Jahr wählen · {yearStart}–{yearStart + 11}
            </span>
            <button
              type="button"
              aria-label="Nächste Jahre"
              onClick={() => setYearStart((value) => value + 12)}
              className="min-h-11 min-w-11 font-mono text-lg"
            >
              ›
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {Array.from({ length: 12 }, (_, index) => yearStart + index).map(
              (year) =>
                (() => {
                  const unavailable =
                    (minDate && year < minDate.getFullYear()) ||
                    (maxDate && year > maxDate.getFullYear());
                  return (
                    <button
                      key={year}
                      type="button"
                      aria-pressed={displayMonth.getFullYear() === year}
                      disabled={!!unavailable}
                      onClick={() => selectYear(year)}
                      className={`min-h-11 border border-ink px-2 font-mono text-sm font-bold disabled:opacity-25 ${displayMonth.getFullYear() === year ? 'bg-ink text-paper' : 'hover:bg-zinc-200'}`}
                    >
                      {year}
                    </button>
                  );
                })()
            )}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-t border-ink pt-3 mt-2">
        <button
          type="button"
          onClick={() => closePicker(false)}
          className="min-h-12 flex-1 border border-ink font-mono text-xs font-bold uppercase hover:bg-zinc-200"
        >
          Abbrechen
        </button>
        <button
          type="button"
          disabled={!draft}
          onClick={() => closePicker(true)}
          className="min-h-12 flex-1 border border-ink bg-ink text-paper font-mono text-xs font-bold uppercase disabled:opacity-40"
        >
          Datum übernehmen
        </button>
      </div>
    </dialog>
  ) : null;

  return (
    <div
      className={`relative ${triggerOnly ? 'shrink-0' : 'w-full'} ${className}`}
    >
      {!triggerOnly && (
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          aria-required={ariaRequired}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={inputClassName}
        />
      )}
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        onClick={openPicker}
        className={`${triggerOnly ? 'inline-flex min-w-11 items-center justify-center' : 'absolute right-0 top-0 h-full'} min-h-11 ${buttonClassName}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="square"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="16" y1="2" x2="16" y2="6" />
        </svg>
      </button>
      {dialog}
    </div>
  );
}
